// Accounts, the balances statement, and the FX table the UI converts with.

import type {
  BalancesResponse,
  UpdateAccountInput,
  NetWorthResponse,
} from "@/finance/model/wire.js";
import type { FxObservation } from "@/finance/pricing/model/fx.js";
import { api, kept, qk } from "@/platform/client";
import { buildFxTable } from "@/finance/pricing/model/fx.js";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// ---- accounts / balances ----
export function useBalances() {
  return useQuery({
    queryKey: qk.balances.all,
    queryFn: () => api.get<BalancesResponse>("/balances"),
  });
}

/** Net worth on every day, slice by slice. Under the balances' key, so every
 *  write that refreshes the balances refreshes it. */
export const netWorthQuery = queryOptions({
  queryKey: qk.balances.netWorth,
  queryFn: () => api.get<NetWorthResponse>("/balances/net-worth"),
  ...kept,
});

export const useNetWorth = () => useQuery(netWorthQuery);

// Raw CHF-quoted FX rates, built into a ready-to-use FxTable once and cached —
// used only for the Balances widgets' own cross-currency totals; buildFxTable
// is isomorphic (same function the server uses) so the conversion math matches
// exactly.
export function useFxTable() {
  return useQuery({
    queryKey: qk.balances.fxRates,
    queryFn: () => api.get<FxObservation[]>("/balances/fx-rates"),
    select: buildFxTable,
  });
}

/** Correcting the account itself — its name, its type, whether it is blocked or
 *  closed. Every page that names an account reads it from the same registry, so
 *  the whole cache is invalidated rather than the balances slice alone. */
export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateAccountInput & { id: number }) =>
      api.patch(`/accounts/${id}`, body),
    onSuccess: () => invalidateAccounts(qc),
  });
}

/** An account's name and identity are read by every finance page — the ledger,
 *  the trades list, the pickers — so a registry edit invalidates all of them
 *  rather than the balances statement alone. */
function invalidateAccounts(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries();
}
